import React, { useState, useMemo } from 'react'
import { Check, ChevronsUpDown, Plus, Star, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useServices, useGalleryServices, useServiceRelations } from '../../hooks/useServices'
import { AddServiceDialog } from '../filters/AddServiceDialog'
import type { GalleryServiceWithDetails } from '../../types'

interface ServiceSelectorProps {
  galleryId: number
  /** Compact mode shows only badges, no dropdown trigger text */
  compact?: boolean
  /** Called when services change (for parent component state sync) */
  onChange?: (services: GalleryServiceWithDetails[]) => void
}

/**
 * Service selector for assigning masonry services to a gallery.
 * Supports multiple services with one marked as primary.
 *
 * @see useServices - fetches available services
 * @see useGalleryServices - fetches current gallery's services
 * @see useServiceRelations - mutations for add/remove/setPrimary
 */
export function ServiceSelector({ galleryId, compact = false, onChange }: ServiceSelectorProps) {
  const [open, setOpen] = useState(false)
  const [showAddDialog, setShowAddDialog] = useState(false)

  // Fetch all available services
  const { data: allServices = [], isLoading: servicesLoading } = useServices()

  // Fetch services currently assigned to this gallery
  const { data: galleryServices = [], isLoading: galleryServicesLoading } = useGalleryServices(galleryId)

  // Mutations for managing relationships
  const { add, remove, setPrimary } = useServiceRelations(galleryId)

  // Get IDs of currently assigned services
  const assignedServiceIds = useMemo(
    () => new Set(galleryServices.map(gs => gs.service_id)),
    [galleryServices]
  )

  // Find primary service
  const primaryServiceId = useMemo(
    () => galleryServices.find(gs => gs.is_primary)?.service_id,
    [galleryServices]
  )

  // Handle adding a service
  const handleAddService = (serviceId: string) => {
    if (assignedServiceIds.has(serviceId)) return

    // If no services yet, make this one primary
    const isPrimary = galleryServices.length === 0
    add.mutate({ serviceId, isPrimary }, {
      onSuccess: () => {
        // Keep popover open for multiple selections
      }
    })
  }

  // Handle removing a service
  const handleRemoveService = (serviceId: string) => {
    remove.mutate({ serviceId })
  }

  // Handle setting primary
  const handleSetPrimary = (serviceId: string) => {
    if (primaryServiceId === serviceId) return
    setPrimary.mutate({ serviceId })
  }

  // Handle new service created via dialog
  const handleServiceCreated = (serviceId: string) => {
    // Auto-add the newly created service to this gallery
    const isPrimary = galleryServices.length === 0
    add.mutate({ serviceId, isPrimary })
  }

  const isLoading = servicesLoading || galleryServicesLoading
  const isMutating = add.isPending || remove.isPending || setPrimary.isPending

  return (
    <div className="space-y-2">
      {/* Assigned Services Display */}
      {galleryServices.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {galleryServices.map((gs) => (
            <Badge
              key={gs.service_id}
              variant={gs.is_primary ? 'default' : 'secondary'}
              className={cn(
                'flex items-center gap-1 pr-1',
                gs.is_primary && 'bg-primary'
              )}
            >
              {gs.is_primary && <Star className="h-3 w-3" />}
              <span>{gs.service.title}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-destructive/20"
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemoveService(gs.service_id)
                }}
                disabled={isMutating}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      {/* Service Picker */}
      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn(
                'justify-between',
                compact ? 'w-auto' : 'w-full max-w-xs'
              )}
              disabled={isLoading}
            >
              {compact ? (
                <Plus className="h-4 w-4" />
              ) : (
                <>
                  <span className="text-muted-foreground">
                    {galleryServices.length === 0
                      ? 'Assign services...'
                      : 'Add more services...'}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <Command>
              <CommandInput placeholder="Search services..." />
              <CommandList>
                <CommandEmpty>
                  No service found.
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => {
                      setOpen(false)
                      setShowAddDialog(true)
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create new service
                  </Button>
                </CommandEmpty>
                <CommandGroup>
                  {allServices.map((service) => {
                    const isAssigned = assignedServiceIds.has(service.id)
                    const isPrimary = primaryServiceId === service.id

                    return (
                      <CommandItem
                        key={service.id}
                        value={service.title}
                        onSelect={() => {
                          if (isAssigned) {
                            // If already assigned, clicking sets as primary
                            if (!isPrimary) {
                              handleSetPrimary(service.id)
                            }
                          } else {
                            handleAddService(service.id)
                          }
                        }}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <Check
                            className={cn(
                              'h-4 w-4',
                              isAssigned ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <span>{service.title}</span>
                        </div>
                        {isAssigned && (
                          <Badge
                            variant={isPrimary ? 'default' : 'outline'}
                            className="text-xs"
                          >
                            {isPrimary ? 'Primary' : 'Click to set primary'}
                          </Badge>
                        )}
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      setOpen(false)
                      setShowAddDialog(true)
                    }}
                    className="text-muted-foreground"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create new service
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Quick info when no services assigned */}
        {galleryServices.length === 0 && !compact && (
          <span className="text-xs text-muted-foreground">
            No services assigned
          </span>
        )}
      </div>

      {/* Add Service Dialog */}
      <AddServiceDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onCreated={handleServiceCreated}
      />
    </div>
  )
}

export default ServiceSelector
